/* eslint-disable max-statements */

import { Divider, Grid, Group, LoadingOverlay, ScrollArea, Stack, Tabs, Text, UnstyledButton } from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Batch, BatchBrokerStatusEnum, Declaration, DeclarationStatus, EnvironmentCountries } from '@e-origin/shared';

import { rotatedSendIcon, sendIcon } from '@assets';
import { BackButton, EucdmToggle, TextInput } from '@components';
import { ContentLayout } from '@features/layout';
import { useDeclaration } from '@hooks/declaration';
import { useGetDeclarationGroupsIds } from '@hooks/declaration/retrieve';
import { useUser } from '@hooks/user/useUser';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';
import { useBuildDeclarationViewTabsData } from './useBuildDeclarationViewTabsData';

export const DeclarationView = () => {
  const [editDisabled, setEditDisabled] = useState(false);
  const { data: settings } = useSettings();
  const { data: user } = useUser();

  const navigate = useNavigate();
  const { tabId, id } = useParams();
  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(new URLSearchParams(routeLocation.search));
  const batchId = queryParams.current.get('batchId');
  const lrn = queryParams.current.get('lrn');
  const listTab = queryParams.current.get('listTab');
  const sourceParam = queryParams.current.get('source');
  const allStatuses = queryParams.current.get('all-statuses') === 'true';
  const [backPath, setBackPath] = useState(
    `/declarations${listTab ? '?tab=' + listTab : ''}${batchId ? '&batchId=' + batchId : ''}${lrn ? '&lrn=' + lrn : ''}`,
  );

  const basePath = `/declarations/${id}`;

  const { data: declarationDetails, isLoading } = useDeclaration(id);

  useEffect(() => {
    if (sourceParam === 'batches') {
      setBackPath('/batches');
    } else if (sourceParam === 'declaration-groups' && !!declarationDetails) {
      setBackPath(`/declaration-groups?batchId=${(declarationDetails?.messageInfo?.batch as unknown as Batch)?._id}`);
    }
  }, [sourceParam, listTab, batchId, lrn, declarationDetails]);

  const { data: rejectedDeclarations } = useGetDeclarationGroupsIds({
    batchId: (declarationDetails?.messageInfo?.batch as unknown as Batch)?._id,
    status: allStatuses ? undefined : declarationDetails?.generalInfo?.status,
    enabled: !!declarationDetails,
  });

  const [relatedDeclarations, currentDeclarationIndex] = useMemo(() => {
    const declarationLRN = declarationDetails?.messageInfo?.LRN;
    if (!rejectedDeclarations || !declarationLRN) return [[], -1];

    if (rejectedDeclarations[declarationLRN]) {
      const declarationsWithSameLRN = rejectedDeclarations[declarationLRN];
      const index = declarationsWithSameLRN.findIndex((declId) => declId === id);

      return [declarationsWithSameLRN, index];
    }

    return [[], -1];
  }, [rejectedDeclarations, declarationDetails]);

  const [allLRNs, currentLRNIndex] = useMemo(() => {
    const declarationLRN = declarationDetails?.messageInfo?.LRN;
    if (!rejectedDeclarations || !declarationLRN) return [[], -1];

    const lrnKeys = Object.keys(rejectedDeclarations);
    const lrnIndex = lrnKeys.indexOf(declarationLRN);

    return [lrnKeys, lrnIndex];
  }, [rejectedDeclarations, declarationDetails]);

  const calculateIndex = (currentIndex: number, arrayLength: number, direction: 'previous' | 'next'): number => {
    return direction === 'previous' ? (currentIndex <= 0 ? arrayLength - 1 : currentIndex - 1) : currentIndex >= arrayLength - 1 ? 0 : currentIndex + 1;
  };

  const navigateToDeclarationLine = (direction: 'previous' | 'next') => {
    if (relatedDeclarations.length <= 1) return;

    const newIndex = calculateIndex(currentDeclarationIndex, relatedDeclarations.length, direction);

    const targetDeclarationId = relatedDeclarations[newIndex];
    navigate(`/declarations/${targetDeclarationId}/${tabId || availableTabs[0]?.value}${routeLocation.search}`, { replace: true });
  };

  const navigateToDeclaration = (direction: 'previous' | 'next') => {
    if (allLRNs.length <= 1) return;

    const newIndex = calculateIndex(currentLRNIndex, allLRNs.length, direction);

    const targetLRN = allLRNs[newIndex];
    const firstDeclarationId = rejectedDeclarations[targetLRN][0];
    navigate(`/declarations/${firstDeclarationId}/${tabId || availableTabs[0]?.value}${routeLocation.search}`, { replace: true });
  };

  const extractStatus = (details?: Declaration): string | undefined => {
    if (!details?.generalInfo) {
      return undefined;
    }

    const hasInvalidationRequest =
      details?.customsState?.invalidationRequest?.invalidationRequestDate && details?.customsState?.invalidationRequest?.invalidationRequestDate.length > 0;

    switch (details.generalInfo.status) {
      case DeclarationStatus.NOT_SENT:
        return 'Not sent';
      case DeclarationStatus.SENT:
        return 'Sent';
      case DeclarationStatus.BEGATE_SENT:
        return 'BE-Gate Sent';
      case DeclarationStatus.BEGATE_RELEASED:
        return 'BE-Gate Released';
      case DeclarationStatus.BEGATE_NOT_RELEASED:
        return 'BE-Gate Not Released';
      case DeclarationStatus.SENDING:
        return 'Sending';
      case DeclarationStatus.ACCEPTED:
        return hasInvalidationRequest ? 'Invalidation request' : 'Accepted';
      case DeclarationStatus.IN_CONTROL:
        return 'In control';
      case DeclarationStatus.INVALIDATED:
        return 'Invalidated';
      case DeclarationStatus.NOT_RELEASED:
        return 'Not released';
      case DeclarationStatus.REJECTED:
        return 'Rejected';
      case DeclarationStatus.EXPORTED:
        return 'Exported';
      case DeclarationStatus.ANALYSIS:
        return 'Analysis';
      case DeclarationStatus.RELEASED:
        return hasInvalidationRequest ? 'Invalidation request' : 'Released';
      default:
        break;
    }
  };

  const status = useMemo(() => extractStatus(declarationDetails), [declarationDetails]);

  const isHighValue = isHighValueDeclaration(declarationDetails?.generalInfo?.group);
  const isHighValueBE = settings.country === EnvironmentCountries.BE && isHighValue;

  useEffect(() => {
    setEditDisabled(
      ![
        DeclarationStatus.ANALYSIS,
        DeclarationStatus.NOT_SENT,
        DeclarationStatus.BEGATE_SENT,
        DeclarationStatus.BEGATE_RELEASED,
        DeclarationStatus.BEGATE_NOT_RELEASED,
        DeclarationStatus.REJECTED,
      ].includes(declarationDetails?.generalInfo?.status) ||
        (settings.isForwarder && (declarationDetails.messageInfo.batch as unknown as Batch).status.brokerStatus === BatchBrokerStatusEnum.SENT),
    );
  }, [declarationDetails]);

  const [tabs, setTabs] = useState(null);

  useEffect(() => {
    if (declarationDetails) {
      const { builtTabs } = useBuildDeclarationViewTabsData({
        declarationDetails,
        editDisabled,
        settings,
        isHighValue,
        isHighValueBE,
        status,
        user,
      });
      setTabs(builtTabs);
    }
  }, [declarationDetails, editDisabled, user]);

  const availableTabs = useMemo(() => tabs?.filter((tab) => tab.condition) || [], [tabs]);

  useEffect(() => {
    if (!tabId && availableTabs.length > 0) {
      navigate(`${basePath}/${availableTabs[0].value}`, { replace: true });
    }
  }, [tabId, availableTabs, navigate]);

  const handleTabChange = (value: string) => {
    navigate(`${basePath}/${value}`, { replace: true });
  };

  const currentTab = availableTabs.find((tab) => tab.value === tabId);

  if (tabId && !currentTab && availableTabs.length > 0) {
    return <Navigate to={`${basePath}/${availableTabs[0].value}`} replace />;
  }

  if (!declarationDetails) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <ContentLayout>
      {/* todo: Check why it works without ContentLayout.Header */}
      <Stack px="xl" gap="xl" mb="md" mx="xl">
        <Group justify="space-between" align="flex-start">
          <Stack>
            <BackButton backPath={backPath} replace></BackButton>
            <Group>
              <Stack mr="lg" gap="xs">
                <Text size="xs">Batch name</Text>
                <Text size="xl" fw={700} lh="xs">
                  {(declarationDetails?.messageInfo?.batch as unknown as Batch)?.name}
                </Text>
              </Stack>
              <Stack mr="lg" gap="xs">
                <Text size="xs">Status</Text>
                <Text size="xl" fw={700} c="blue" lh="xs">
                  {status}
                </Text>
              </Stack>
              <Stack>
                <EucdmToggle label="Toggle view: EUCDM code hints" size="sm" />
              </Stack>

              {declarationDetails?.messageInfo.amended?.LRN && (
                <Stack mr="lg" gap="xs">
                  <Text size="xs">Amended LRN</Text>
                  <Text size="xl" fw={700} c="blue" lh="xs">
                    {declarationDetails?.messageInfo.amended?.LRN}
                  </Text>
                </Stack>
              )}
            </Group>
          </Stack>
          <Stack>
            {allLRNs.length > 1 && (
              <Group justify="space-between" align="center" mt={5}>
                <UnstyledButton onClick={() => navigateToDeclaration('previous')}>
                  <img src={rotatedSendIcon} />
                </UnstyledButton>
                <Text size="lg">Declaration</Text>
                <UnstyledButton onClick={() => navigateToDeclaration('next')}>
                  <img src={sendIcon} />
                </UnstyledButton>
              </Group>
            )}
            {relatedDeclarations.length > 1 && (
              <Group justify="space-between" align="center" mt={5}>
                <UnstyledButton onClick={() => navigateToDeclarationLine('previous')}>
                  <img src={rotatedSendIcon} />
                </UnstyledButton>
                <Text size="lg">Declaration Line</Text>
                <UnstyledButton onClick={() => navigateToDeclarationLine('next')}>
                  <img src={sendIcon} />
                </UnstyledButton>
              </Group>
            )}
          </Stack>
        </Group>
        <Grid>
          <Grid.Col span={3}>
            <TextInput label="Transport" value={declarationDetails?.messageInfo?.transport} disabled />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput label="LRN" value={declarationDetails?.messageInfo?.LRN} disabled />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput label="Sequence" value={declarationDetails?.goodShipmentGlobal?.sequence} disabled />
          </Grid.Col>
          {!isHighValue && (
            <Grid.Col span={3}>
              <TextInput
                label="Deferred payment"
                value={declarationDetails?.messageInfo?.deferredPayment}
                disabled={editDisabled || settings.country === EnvironmentCountries.NL}
              />
            </Grid.Col>
          )}
        </Grid>
        {isHighValue && (
          <Grid>
            <Grid.Col span={2}>
              <TextInput label="Total amount invoiced" value={declarationDetails?.goodShipmentGlobal?.totalAmountInvoiced || ''} disabled />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput label="Statistical value" value={declarationDetails?.otherDataElements?.statisticalValue || ''} disabled />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput label="Gross mass" value={declarationDetails?.goodShipmentGlobal?.totalGrossMass || ''} disabled />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput label="Net mass" value={declarationDetails?.goodShipmentGlobal?.totalNetMass || ''} disabled />
            </Grid.Col>
            <Grid.Col span={2}>
              {' '}
              <TextInput label="Quantity" value={declarationDetails?.goodShipmentGlobal?.totalQuantity || ''} disabled />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput label="Number of packages" value={declarationDetails?.goodShipmentGlobal?.totalNumberOfPackages || ''} disabled />
            </Grid.Col>
          </Grid>
        )}
      </Stack>

      <Divider />

      <ContentLayout.Content>
        <Tabs
          h="100%"
          miw={40}
          value={tabId}
          onChange={handleTabChange}
          defaultValue="goods-shipment"
          orientation="vertical"
          px={25}
          keepMounted={false}
          py="lg"
        >
          <Tabs.List>
            {tabs?.map((tab) =>
              tab.condition ? (
                <Tabs.Tab key={tab.value} value={tab.value} data-pw={`${tab.value}-tab`}>
                  <Group wrap="nowrap">
                    {tab.label}
                    {tab.icon}
                  </Group>
                </Tabs.Tab>
              ) : null,
            )}
          </Tabs.List>
          {tabs?.map((tab) =>
            tab.condition ? (
              <Tabs.Panel key={tab.value} value={tab.value}>
                <ScrollArea h="100%" offsetScrollbars="y" mx="xl">
                  {tab.component}
                </ScrollArea>
              </Tabs.Panel>
            ) : null,
          )}
        </Tabs>
      </ContentLayout.Content>
    </ContentLayout>
  );
};
